/**
 * Auto-generated utility to flatten nested translation messages
 * This is needed because FormatJS expects flat keys when using typed IDs
 * 
 * @example
 * const nested = { title: { main: "Hello" } };
 * const flat = flattenMessages(nested);
 * // Result: { "title.main": "Hello" }
 */

type NestedMessages = Record<string, any>;

/**
 * Flattens nested message object into dot-notation keys
 * 
 * @param messages - Nested messages object from JSON
 * @param prefix - Internal use for recursion
 * @returns Flattened messages object with dot-notation keys
 */
export function flattenMessages(
  messages: NestedMessages,
  prefix: string = ''
): Record<string, string> {
  return Object.keys(messages).reduce((acc: Record<string, string>, key: string) => {
    const value = messages[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      acc[fullKey] = value;
    } else if (typeof value === 'object' && value !== null) {
      Object.assign(acc, flattenMessages(value, fullKey));
    }

    return acc;
  }, {});
}

/**
 * Type-safe version of flattenMessages
 * Use this to avoid TypeScript errors with IntlProvider
 */
export function flattenMessagesTyped<T extends NestedMessages>(
  messages: T
): Record<string, string> {
  return flattenMessages(messages) as any;
}
